import { useState, useEffect, useRef } from "react";
import JSLayout from "./js_layout";
import { API_ENDPOINTS } from "../../api/config";

const profileCss = `
  .mp-layout { display:grid; grid-template-columns:280px 1fr; gap:1.5rem; align-items:start; }
  .mp-avatar-card { background:#fff; border:1px solid rgba(26,26,26,0.07); border-radius:4px; padding:2rem; text-align:center; }
  .mp-big-avatar { width:88px; height:88px; border-radius:50%; background:#1a1a1a; border:3px solid #c9a84c; display:flex; align-items:center; justify-content:center; font-family:'Cormorant Garamond',serif; font-size:2rem; font-weight:600; color:#c9a84c; margin:0 auto 1rem; }
  .mp-name { font-family:'Cormorant Garamond',serif; font-size:1.4rem; font-weight:600; color:#1a1a1a; margin-bottom:2px; }
  .mp-role { font-size:0.8rem; color:#888; font-weight:300; margin-bottom:1rem; }
  .mp-change-photo { width:100%; padding:0.65rem; background:none; border:1px dashed rgba(26,26,26,0.15); border-radius:2px; font-family:'DM Sans',sans-serif; font-size:0.72rem; color:#aaa; cursor:pointer; transition:all 0.2s; margin-bottom:1.25rem; }
  .mp-change-photo:hover { border-color:#c9a84c; color:#c9a84c; }
  .mp-completion { }
  .mp-completion-label { display:flex; justify-content:space-between; font-size:0.7rem; color:#aaa; margin-bottom:5px; }
  .mp-completion-pct { color:#c9a84c; font-weight:500; }
  .mp-bar-bg { height:4px; background:rgba(26,26,26,0.08); border-radius:2px; overflow:hidden; }
  .mp-bar-fill { height:100%; background:#c9a84c; border-radius:2px; }
  .mp-section-card { background:#fff; border:1px solid rgba(26,26,26,0.07); border-radius:4px; padding:1.5rem; margin-bottom:1rem; }
  .mp-section-card:last-child { margin-bottom:0; }
  .mp-section-hdr { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.25rem; }
  .mp-section-title { font-family:'Cormorant Garamond',serif; font-size:1.1rem; font-weight:600; color:#1a1a1a; }
  .mp-section-title em { font-style:italic; }
  .mp-edit-btn { background:none; border:1px solid rgba(26,26,26,0.12); border-radius:2px; padding:4px 12px; font-family:'DM Sans',sans-serif; font-size:0.7rem; color:#888; cursor:pointer; transition:all 0.2s; }
  .mp-edit-btn:hover { border-color:#c9a84c; color:#c9a84c; }
  .mp-edit-btn.active { background:#1a1a1a; color:#f5f0e8; border-color:#1a1a1a; }
  .mp-grid2 { display:grid; grid-template-columns:1fr 1fr; gap:1rem; }
  .mp-field { margin-bottom:1rem; }
  .mp-field:last-child { margin-bottom:0; }
  .mp-field-label { font-size:0.65rem; font-weight:500; letter-spacing:0.1em; text-transform:uppercase; color:#aaa; margin-bottom:5px; display:block; }
  .mp-field-value { font-size:0.88rem; color:#1a1a1a; font-weight:300; }
  .mp-skill-wrap { display:flex; flex-wrap:wrap; gap:6px; }
  .mp-skill { font-size:0.75rem; padding:4px 12px; background:#f5f0e8; border:1px solid rgba(26,26,26,0.08); border-radius:2px; color:#555; }
  .mp-add-skill { font-size:0.75rem; padding:4px 12px; background:none; border:1px dashed rgba(201,168,76,0.4); border-radius:2px; color:#c9a84c; cursor:pointer; transition:all 0.2s; }
  .mp-add-skill:hover { background:rgba(201,168,76,0.07); }
  .mp-save-row { display:flex; gap:0.75rem; margin-top:1.25rem; }
  @media(max-width:900px){ .mp-layout{grid-template-columns:1fr;} .mp-big-avatar{width:72px;height:72px;font-size:1.6rem;} }
  @media(max-width:600px){ .mp-grid2{grid-template-columns:1fr;} }
`;


export function MyProfile() {
  const [editing, setEditing] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [countiesList, setCountiesList] = useState([]);
  const [globalSkillsPool, setGlobalSkillsPool] = useState([]);
  const [globalSpecialisations, setGlobalSpecialisations] = useState([]);
  const [skillInput, setSkillInput] = useState("");
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Updated state structure to accept savedJobs from your JobSeekerDashboardDto/Profile configurations
  const [form, setForm] = useState({
    firstName: "", lastName: "", phone: "", city: "",
    bio: "", specialisation: "", yearsExperience: "", availability: "",
    currentUserProfileScore: 0,
    skills: [], 
    certificates: [],
    savedJobs: [] // Added state tracking for saved positions
  });

  useEffect(() => {
    const fetchMetadataAndProfile = async () => {
      const token = localStorage.getItem("token");
      try {
        // 1. Fetch Global Metadata Parallelly
        const [countyRes, skillsRes, specialisationsRes, profileRes] = await Promise.all([
          fetch(`${API_ENDPOINTS.DIMENSIONS}/counties`, { headers: { "Authorization": `Bearer ${token}` } }),
          fetch(`${API_ENDPOINTS.DIMENSIONS}/skills-pool`, { headers: { "Authorization": `Bearer ${token}` } }),
          fetch(`${API_ENDPOINTS.DIMENSIONS}/specialisations`, { headers: { "Authorization": `Bearer ${token}` } }),
          fetch(API_ENDPOINTS.EMPPROFILE, { headers: { "Authorization": `Bearer ${token}` } })
        ]);

        if (countyRes.ok) setCountiesList(await countyRes.json());
        if (skillsRes.ok) setGlobalSkillsPool(await skillsRes.json());
        if (specialisationsRes.ok) setGlobalSpecialisations(await specialisationsRes.json());

        if (profileRes.ok) {
          const data = await profileRes.json();
          setForm({
            ...data,
            skills: data.skills || [],
            certificates: data.certificates || [],
            savedJobs: data.savedJobs || [] 
          });
        }
      } catch (error) {
        // console.error("Initialization error", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetadataAndProfile();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSkillDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- SAVE TO DATABASE ---
  const saveSection = async (section) => {
    try {
      const token = localStorage.getItem("token");

      // Extract ONLY the fields that match the C# JobSeekerProfileUpdateDto structure
      const updatePayload = {
        city: form.city || "",
        bio: form.bio || "",
        specialisation: form.specialisation || "",
        yearsExperience: form.yearsExperience || "",
        availability: form.availability || "",
        certificates: (form.certificates || []).map(c => ({
          certificateName: c.certificateName || "",
          issuingOrganization: c.issuingOrganization || "",
          certificateUrl: c.certificateUrl || ""
        }))
      };

      const response = await fetch(API_ENDPOINTS.UPDATEPROFILE, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(updatePayload) // Send clean payload instead of raw state object
      });

      if (response.ok) {
        const result = await response.json();
        setForm(prev => ({ ...prev, currentUserProfileScore: result.newScore || prev.currentUserProfileScore }));
        setEditing(null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to update profile: ${errorData.message || response.statusText}`);
      }
    } catch (error) {
      alert("Failed to update profile due to an unexpected error.");
    }
  };

  // --- SAVE SKILLS DIRECTLY TO SERVER ---
  const syncSkillsToDatabase = async (updatedSkillsList) => {
    try {
      const token = localStorage.getItem("token");
      
      // Mirror the C# Update DTO layout profile fields
      const updatePayload = {
        city: form.city || "",
        bio: form.bio || "",
        specialisation: form.specialisation || "",
        yearsExperience: form.yearsExperience || "",
        availability: form.availability || "",
        skills: updatedSkillsList, // Sending string list directly to your backend mapping logic
        certificates: form.certificates
      };

      const response = await fetch(API_ENDPOINTS.UPDATEPROFILE, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(updatePayload)
      });

      if (response.ok) {
        const result = await response.json();
        setForm(prev => ({ ...prev, currentUserProfileScore: result.newScore || prev.currentUserProfileScore }));
      }
    } catch (e) {
      alert("Failed syncing skills with your profile record.");
    }
  };

  const handleSelectSkill = (selectedSkill) => {
    // Avoid double entries
    if (form.skills.includes(selectedSkill)) {
      setSkillInput("");
      setShowSkillDropdown(false);
      return;
    }

    const nextSkills = [...form.skills, selectedSkill];
    setForm(prev => ({ ...prev, skills: nextSkills }));
    setSkillInput("");
    setShowSkillDropdown(false);
    
    // Auto sync to database immediately on choosing an item
    syncSkillsToDatabase(nextSkills);
  };

  const handleRemoveSkill = (skillToRemove) => {
    const nextSkills = form.skills.filter(s => s !== skillToRemove);
    setForm(prev => ({ ...prev, skills: nextSkills }));
    syncSkillsToDatabase(nextSkills);
  };

  // Filter skills pool based on text typed
  const filteredSkillsPool = globalSkillsPool.filter(skill => 
    skill.toLowerCase().includes(skillInput.toLowerCase()) &&
    !form.skills.includes(skill)
  );

  // // --- UN-SAVE JOB HANDLER ---
  // const handleRemoveSavedJob = async (jobId) => {
  //   try {
  //     const token = localStorage.getItem("token");
  //     const response = await fetch(`/api/jobseeker/jobs/${jobId}/save`, {
  //       method: "POST",
  //       headers: { "Authorization": `Bearer ${token}` }
  //     });

  //     if (response.ok) {
  //       const result = await response.json();
  //       // If result.saved comes back false, pull it from the list cleanly
  //       if (!result.saved) {
  //         setForm(prev => ({
  //           ...prev,
  //           savedJobs: prev.savedJobs.filter(job => job.id !== jobId)
  //         }));
  //       }
  //     }
  //   } catch (error) {
  //     alert("Failed to update saved job status");
  //   }
  // };

  const addCertificate = () => {
    setForm({
      ...form,
      certificates: [
        ...form.certificates,
        { certificateName: "", issuingOrganization: "" }
      ]
    });
    setEditing("certs"); 
  };

  const handleCertChange = (index, field, value) => {
    const updatedCerts = [...form.certificates];
    updatedCerts[index][field] = value;
    setForm({ ...form, certificates: updatedCerts });
  };

  const removeCert = (index) => {
    const updatedCerts = form.certificates.filter((_, i) => i !== index);
    setForm({ ...form, certificates: updatedCerts });
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;

    // Build the form payload fields explicitly
    const targetCert = form.certificates[index];
    const formData = new FormData();
    formData.append("file", file);
    formData.append("certIndex", index);
    formData.append("certName", targetCert.certificateName || "");
    formData.append("provider", targetCert.issuingOrganization || "");
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.UPLOADCERTIFICATE, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData // Content-Type header is omitted intentionally so browser configures multi-part form bounds
      });

      if (response.ok) {
        // const data = await response.json();
        
        // Re-fetch profile values to cleanly pull data records synced with server state keys
        alert("Certificate uploaded and saved securely!");
        
        // Trigger a fresh state download to populate the fresh hidden IDs for streaming
        const refreshResponse = await fetch(API_ENDPOINTS.EMPPROFILE, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (refreshResponse.ok) {
          const profileData = await refreshResponse.json();
          setForm(prev => ({ ...prev, ...profileData }));
        }
      } else {
        alert("Failed to bind and upload asset.");
      }
    } catch (error) {
      alert("Upload failed. Please try again.");
    }
  };

  const handleCvUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.UPLOADCV, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json(); 
        setForm({ 
          ...form, 
          cvUrl: data.url, 
          cvUpdatedAt: data.updatedAt,
          currentUserProfileScore: data.newScore 
        });
        alert("CV Uploaded successfully!");
      }
    } catch (error) {
      // console.error("CV upload error:", error);
    }
  };

  // --- SECURE FILE STREAM VIEW HANDLER ---
  const handleViewDocument = async (certId) => {
    try {
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${API_ENDPOINTS.VIEWCERTIFICATE}/${certId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error("Session expired or unauthorized access.");
        throw new Error("Could not locate the document asset on the server.");
      }

      const fileBlob = await response.blob();
      const secureBlobUrl = window.URL.createObjectURL(fileBlob);
      window.open(secureBlobUrl, "_blank");
      
      setTimeout(() => window.URL.revokeObjectURL(secureBlobUrl), 200);      
    } catch (error) {
      alert(error.message || "An error occurred while attempting to render the document.");
    }
  };
  const handleViewCV = async () => {
    try {
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${API_ENDPOINTS.VIEWCV}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error("Session expired or unauthorized access.");
        throw new Error("Could not locate the document asset on the server.");
      }

      const fileBlob = await response.blob();
      const secureBlobUrl = window.URL.createObjectURL(fileBlob);
      window.open(secureBlobUrl, "_blank");
      
      setTimeout(() => window.URL.revokeObjectURL(secureBlobUrl), 200);      
    } catch (error) {
      alert(error.message || "An error occurred while attempting to render the document.");
    }
  };

  const handleInput = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  if (typeof document !== "undefined" && !document.getElementById("bl-mp-css")) {
    const s = document.createElement("style"); s.id="bl-mp-css"; s.textContent=profileCss; document.head.appendChild(s);
  }

    if (loading) return <JSLayout title="Loading..."><div>Loading Profile...</div></JSLayout>;


  return (
    <JSLayout title="My" titleEm="Profile">
      <div className="mp-layout">
        
        {/* LEFT COLUMN: STATIC VIEW OF IDENTITY */}
        <aside>
          <div className="mp-avatar-card">
            <div className="mp-big-avatar">{form.firstName?.[0]}{form.lastName?.[0]}</div>
            <div className="mp-name">{form.firstName} {form.lastName}</div>
            <div className="mp-role">{form.specialisation || "Professional"}</div>
            
            <div className="mp-completion" style={{marginTop: '1.5rem'}}>
              <div className="mp-completion-label">
                <span>Profile strength</span>
                <span className="mp-completion-pct">{form.currentUserProfileScore}%</span>
              </div>
              <div className="mp-bar-bg">
                <div className="mp-bar-fill" style={{ width: `${form.currentUserProfileScore}%` }} />
              </div>
            </div>
          </div>
        </aside>

        {/* RIGHT COLUMN: INTERACTIVE SECTIONS */}
        <div className="mp-main-col">
          
          {/* PERSONAL INFO SECTION */}
          <div className="mp-section-card">
            <div className="mp-section-hdr">
              <div className="mp-section-title">Personal <em>Information</em></div>
              <button className="mp-edit-btn" onClick={() => setEditing(editing === 'personal' ? null : 'personal')}>
                {editing === 'personal' ? 'Cancel' : 'Edit'}
              </button>
            </div>

            {editing === 'personal' ? (
              <div className="mp-edit-form">
                <div className="mp-grid2">
                  <div className="bl-field">
                    <label className="bl-label">First Name (Locked)</label>
                    <input className="bl-input" value={form.firstName} disabled style={{ background: '#f9f9f9', cursor: 'not-allowed' }} />
                  </div>
                  <div className="bl-field">
                    <label className="bl-label">Last Name (Locked)</label>
                    <input className="bl-input" value={form.lastName} disabled style={{ background: '#f9f9f9', cursor: 'not-allowed' }} />
                  </div>
                  <div className="bl-field">
                    <label className="bl-label">Phone (Locked)</label>
                    <input className="bl-input" value={form.phone} disabled style={{ background: '#f9f9f9', cursor: 'not-allowed' }} />
                  </div>
                  <div className="bl-field">
                    <label className="bl-label">Location (County)</label>
                    <select 
                      className="bl-select" 
                      value={form.city} 
                      onChange={handleInput("city")}
                    >
                      <option value="">Select your Location...</option>
                      {countiesList.map(county => (
                        <option key={county.id} value={county.name}>{county.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mp-save-row">
                  <button className="bl-btn-primary" onClick={() => saveSection('personal')}>Save City Change</button>
                </div>
              </div>
            ) : (
              <div className="mp-grid2">
                <div className="mp-field"><span className="mp-field-label">First Name</span><span className="mp-field-value">{form.firstName}</span></div>
                <div className="mp-field"><span className="mp-field-label">Last Name</span><span className="mp-field-value">{form.lastName}</span></div>
                <div className="mp-field"><span className="mp-field-label">Phone</span><span className="mp-field-value">{form.phone || "Not set"}</span></div>
                <div className="mp-field"><span className="mp-field-label">Location / County</span><span className="mp-field-value">{form.city || "Not set"}</span></div>
              </div>
            )}
          </div>

          {/* PROFESSIONAL DETAILS SECTION */}
          <div className="mp-section-card">
            <div className="mp-section-hdr">
              <div className="mp-section-title">Professional <em>Details</em></div>
              <button className="mp-edit-btn" onClick={() => setEditing(editing === 'prof' ? null : 'prof')}>
                {editing === 'prof' ? 'Cancel' : 'Edit'}
              </button>
            </div>

            {editing === 'prof' ? (
              <div className="mp-edit-form">
                <div className="bl-field">
                  <label className="bl-label">Bio / About</label>
                  <textarea className="bl-textarea" value={form.bio} onChange={handleInput("bio")} rows={4} />
                </div>
                <div className="mp-grid2">
                  <div className="bl-field">
                    <label className="bl-label">Specialisation</label>
                    {/* <select 
                      className="bl-select" 
                      value={form.city} 
                      onChange={handleInput("city")}
                    >
                      <option value="">Select your Location...</option>
                      {countiesList.map(county => (
                        <option key={county.id} value={county.name}>{county.name}</option>
                      ))}
                    </select> */}
                    <select className="bl-select" value={form.specialisation} onChange={handleInput("specialisation")}>
                      {globalSpecialisations.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="bl-field"><label className="bl-label">Years Experience</label><input className="bl-input" value={form.yearsExperience} onChange={handleInput("yearsExperience")} /></div>
                </div>
                <div className="mp-save-row">
                   <button className="bl-btn-primary" onClick={() => saveSection('prof')}>Save Profile</button>
                </div>
              </div>
            ) : (
              <>
                <div className="mp-field"><span className="mp-field-label">Bio</span><p className="mp-field-value" style={{lineHeight: 1.6}}>{form.bio || "No bio added yet."}</p></div>
                <div className="mp-grid2">
                  <div className="mp-field"><span className="mp-field-label">Specialisation</span><span className="mp-field-value">{form.specialisation}</span></div>
                  <div className="mp-field"><span className="mp-field-label">Experience</span><span className="mp-field-value">{form.yearsExperience}</span></div>
                </div>
              </>
            )}
          </div>

          {/* SKILLS SECTION */}
          <div className="mp-section-card">
            <div className="mp-section-hdr">
              <div className="mp-section-title">Skills & <em>Expertise</em></div>
            </div>
            
            {/* Active Tags Display */}
            <div className="mp-skill-wrap" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.25rem' }}>
              {form.skills.map((skillName, i) => (
                <span key={i} className="mp-skill" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '6px 12px', background: '#f5f5f5', borderRadius: '20px', fontSize: '0.85rem' }}>
                  {skillName}
                  <button 
                    type="button" 
                    onClick={() => handleRemoveSkill(skillName)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#888', fontSize: '0.85rem', padding: 0 }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            {/* Auto-suggest Search Entry Engine */}
            <div ref={dropdownRef} style={{ position: 'relative', maxWidth: '400px' }}>
              <input 
                type="text"
                className="bl-input"
                placeholder="Type to search and add skills (e.g. Acrylics, Balayage)..."
                value={skillInput}
                onChange={(e) => {
                  setSkillInput(e.target.value);
                  setShowSkillDropdown(true);
                }}
                onFocus={() => setShowSkillDropdown(true)}
              />
              
              {showSkillDropdown && skillInput.trim().length > 0 && (
                <div style={{ 
                  position: 'absolute', top: '100%', left: 0, right: 0, 
                  background: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10, max穩eight: '200px', overflowY: 'auto'
                }}>
                  {filteredSkillsPool.length > 0 ? (
                    filteredSkillsPool.map((pooledSkill, idx) => (
                      <div 
                        key={idx}
                        onClick={() => handleSelectSkill(pooledSkill)}
                        style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f9f9f9' }}
                        onMouseEnter={(e) => e.target.style.background = '#fafafa'}
                        onMouseLeave={(e) => e.target.style.background = '#fff'}
                      >
                        {pooledSkill}
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '10px 14px', color: '#999', fontSize: '0.85rem' }}>No matching skills found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* CERTIFICATES SECTION */}
          <div className="mp-section-card">
            <div className="mp-section-hdr">
              <div className="mp-section-title">Certifications & <em>Awards</em></div>
              <button 
                className={`mp-edit-btn ${editing === 'certs' ? 'active' : ''}`} 
                onClick={() => setEditing(editing === 'certs' ? null : 'certs')}
              >
                {editing === 'certs' ? 'Done' : 'Edit'}
              </button>
            </div>

            {editing === 'certs' ? (
              <div className="mp-edit-form">
                {form.certificates.map((cert, i) => (
                  <div key={i} className="mp-cert-edit-row" style={{ 
                    padding: '1.25rem', border: '1px solid #f0f0f0', borderRadius: '4px', marginBottom: '1rem' 
                  }}>
                    <div className="mp-grid2">
                      <div className="bl-field">
                        <label className="bl-label">Certificate Name</label>
                        <input 
                          className="bl-input" 
                          value={cert.certificateName} 
                          onChange={(e) => handleCertChange(i, "certificateName", e.target.value)} 
                        />
                      </div>
                      <div className="bl-field">
                        <label className="bl-label">Issuing Organization</label>
                        <input 
                          className="bl-input" 
                          value={cert.issuingOrganization} 
                          onChange={(e) => handleCertChange(i, "issuingOrganization", e.target.value)} 
                        />
                      </div>
                    </div>

                    <div className="bl-field" style={{marginTop: '1rem'}}>
                      <label className="bl-label">Upload Certificate (PDF or Image)</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <input 
                          type="file" 
                          onChange={(e) => handleFileUpload(i, e.target.files[0])}
                          style={{ fontSize: '0.75rem' }}
                        />
                        {cert.certificateUrl && (
                          <span style={{ fontSize: '0.7rem', color: '#27ae60' }}>✓ Uploaded</span>
                        )}
                      </div>
                    </div>

                    <button 
                      onClick={() => removeCert(i)}
                      style={{ marginTop: '1rem', background: 'none', border: 'none', color: '#ff4d4f', cursor: 'pointer', fontSize: '0.7rem', padding: 0 }}
                    >
                      Remove this entry
                    </button>
                  </div>
                ))}
                <button className="mp-add-skill" onClick={addCertificate}>+ Add another</button>
              </div>
            ) : (
              <div className="mp-cert-list">
                {form.certificates.map((cert, i) => (
                  <div key={i} style={{ marginBottom: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div className="mp-field-value" style={{ fontWeight: 600 }}>{cert.certificateName}</div>
                      <div className="mp-role" style={{marginBottom: 0}}>{cert.issuingOrganization}</div>
                    </div>
                    
                    {/* Route through the localized JavaScript function instead of a direct href link */}
                    {cert.id && (
                      <button 
                        type="button"
                        onClick={() => handleViewDocument(cert.id)} 
                        className="mp-edit-btn"
                        style={{ 
                          background: 'none', 
                          border: '1px solid #1a1a1a', 
                          cursor: 'pointer',
                          padding: '6px 14px',
                          borderRadius: '4px'
                        }}
                      >
                        View Document
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* NEW: SAVED JOBS ECOSYSTEM COMPONENT
          <div className="mp-section-card">
            <div className="mp-section-hdr">
              <div className="mp-section-title">Saved <em>Opportunities</em></div>
              <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: 500 }}>
                {form.savedJobs.length} {form.savedJobs.length === 1 ? 'position' : 'positions'}
              </span>
            </div>

            {form.savedJobs.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {form.savedJobs.map((job) => (
                  <div 
                    key={job.id} 
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '1.25rem',
                      border: '1px solid #f5f5f5',
                      borderRadius: '6px',
                      background: '#fff'
                    }}
                  >
                    <div>
                      <div className="mp-field-value" style={{ fontWeight: 600, fontSize: '0.95rem' }}>{job.title}</div>
                      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '0.2rem' }}>
                        <span className="mp-role" style={{ marginBottom: 0, fontSize: '0.8rem' }}>{job.company}</span>
                        <span style={{ color: '#ccc', fontSize: '0.8rem' }}>•</span>
                        <span style={{ color: '#666', fontSize: '0.8rem' }}>{job.location}</span>
                      </div>
                      <div style={{ fontSize: '0.8rem', fontWeight: 500, color: '#1a1a1a', marginTop: '0.4rem' }}>
                        {job.salaryRange}
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => handleRemoveSavedJob(job.id)}
                      className="mp-edit-btn"
                      style={{ 
                        color: '#ff4d4f', 
                        borderColor: 'transparent',
                        background: 'rgba(255,77,79,0.04)',
                        padding: '6px 12px',
                        borderRadius: '4px'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#aaa', fontSize: '0.85rem' }}>
                No jobs saved yet. Positions you bookmark while browsing will appear here.
              </div>
            )}
          </div> */}

          {/* CV / RESUME SECTION */}
          <div className="mp-section-card">
            <div className="mp-section-hdr">
              <div className="mp-section-title">Curriculum <em>Vitae</em></div>
              {form.cvUrl && (
                <span style={{ fontSize: '0.7rem', color: '#27ae60', fontWeight: 500 }}>
                  ✓ CV ATTACHED
                </span>
              )}
            </div>

            <div style={{ 
              border: '1px dashed rgba(26,26,26,0.1)', 
              borderRadius: '4px', 
              padding: '2rem', 
              textAlign: 'center',
              background: form.cvUrl ? '#f9fbf9' : '#fafafa'
            }}>
              {form.cvUrl ? (
                <div>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📄</div>
                  <div className="mp-field-value" style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                    Your CV is active
                  </div>
                  <div className="mp-role" style={{ marginBottom: '1.25rem' }}>
                    Last updated: {form.cvUpdatedAt ? new Date(form.cvUpdatedAt).toLocaleDateString() : 'Recently'}
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                    {form.cvUrl && (
                      <button 
                        type="button"
                        onClick={() => handleViewCV()} 
                        className="bl-btn-ghost" style={{ padding: '8px 16px', fontSize: '0.75rem' }}
                      >
                        View Current CV
                      </button>
                    )}
                    <label className="mp-edit-btn" style={{ cursor: 'pointer', display: 'inline-block' }}>
                      Replace File
                      <input type="file" hidden onChange={(e) => handleCvUpload(e.target.files[0])} accept=".pdf,.doc,.docx" />
                    </label>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📤</div>
                  <div className="mp-field-value" style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                    Upload your CV
                  </div>
                  <p className="mp-role" style={{ maxWidth: '300px', margin: '0 auto 1.25rem' }}>
                    Increase your profile strength by 30% and get noticed by top employers.
                  </p>
                  <label className="bl-btn-primary" style={{ cursor: 'pointer', display: 'inline-block' }}>
                    Select File
                    <input type="file" hidden onChange={(e) => handleCvUpload(e.target.files[0])} accept=".pdf,.doc,.docx" />
                  </label>
                  <div style={{ marginTop: '0.75rem', fontSize: '0.65rem', color: '#aaa' }}>
                    Accepted formats: PDF, Word (Max 5MB)
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </JSLayout>
  );
}